import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { 
  Send, Building2, Users, MessageSquare, Clock, CheckCircle,
  AlertCircle, Mail, Bell, Smartphone
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BroadcastPage = () => {
  const { getAuthHeader } = useAuth();
  const [schools, setSchools] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    target_schools: [],
    channels: ['in_app']
  });
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [schoolsRes, announcementsRes] = await Promise.all([
        axios.get(`${API_URL}/api/schools`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/super-admin/announcements`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setSchools(schoolsRes.data);
      setAnnouncements(announcementsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolToggle = (schoolId) => {
    const newTargets = formData.target_schools.includes(schoolId)
      ? formData.target_schools.filter(id => id !== schoolId)
      : [...formData.target_schools, schoolId];
    
    setFormData({ ...formData, target_schools: newTargets });
    setSelectAll(newTargets.length === 0);
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setFormData({ ...formData, target_schools: schools.map(s => s.id) });
      setSelectAll(false);
    } else {
      setFormData({ ...formData, target_schools: [] });
      setSelectAll(true);
    }
  };

  const handleChannelToggle = (channel) => {
    const newChannels = formData.channels.includes(channel)
      ? formData.channels.filter(c => c !== channel)
      : [...formData.channels, channel];
    setFormData({ ...formData, channels: newChannels });
  };

  const sendBroadcast = async () => {
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please enter subject and message');
      return;
    }
    if (formData.channels.length === 0) {
      toast.error('Please select at least one channel');
      return;
    }

    setSending(true);
    try {
      // Build params - FastAPI needs arrays passed correctly
      const params = new URLSearchParams();
      params.append('subject', formData.subject);
      params.append('message', formData.message);
      
      // Handle target_schools - if selectAll is true, send empty (all schools)
      // if selectAll is false, send selected schools
      const targetSchools = selectAll ? [] : formData.target_schools;
      targetSchools.forEach(schoolId => {
        params.append('target_schools', schoolId);
      });
      
      // Handle channels array
      formData.channels.forEach(channel => {
        params.append('channels', channel);
      });
      
      const response = await axios.post(
        `${API_URL}/api/super-admin/broadcast?${params.toString()}`,
        null,
        { headers: getAuthHeader() }
      );
      
      toast.success(`Broadcast sent to ${response.data.recipients} recipients!`);
      setFormData({ subject: '', message: '', target_schools: [], channels: ['in_app'] });
      setSelectAll(true);
      fetchData();
    } catch (error) {
      console.error('Broadcast error:', error);
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const channelOptions = [
    { id: 'in_app', label: 'In-App Notification', icon: Bell },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'sms', label: 'SMS', icon: Smartphone },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare }
  ];

  return (
    <DashboardLayout title="Broadcast Messages">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Message */}
        <div className="lg:col-span-2 space-y-6">
          <Card data-testid="compose-broadcast-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-primary" />
                Compose Broadcast
              </CardTitle>
              <CardDescription>Send announcements to all or selected schools</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Enter message subject..."
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  data-testid="broadcast-subject-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="Enter your broadcast message..."
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="broadcast-message-input"
                />
              </div>

              {/* Channels */}
              <div className="space-y-2">
                <Label>Delivery Channels</Label>
                <div className="flex flex-wrap gap-3">
                  {channelOptions.map(channel => {
                    const Icon = channel.icon;
                    const isSelected = formData.channels.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        onClick={() => handleChannelToggle(channel.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          isSelected 
                            ? 'bg-primary text-primary-foreground border-primary' 
                            : 'bg-muted/50 hover:bg-muted border-border'
                        }`}
                        data-testid={`channel-${channel.id}`}
                      >
                        <Icon className="h-4 w-4" />
                        {channel.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: Email, SMS and WhatsApp require configured providers in Settings
                </p>
              </div>

              <Button 
                onClick={sendBroadcast} 
                disabled={sending}
                className="w-full"
                data-testid="send-broadcast-btn"
              >
                <Send className={`h-4 w-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
                {sending ? 'Sending...' : 'Send Broadcast'}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Announcements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Recent Announcements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No announcements yet</p>
              ) : (
                <div className="space-y-4">
                  {announcements.slice(0, 5).map((announcement, index) => (
                    <motion.div
                      key={announcement.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 border rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{announcement.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{announcement.message}</p>
                        </div>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(announcement.created_at).toLocaleDateString()}
                        </Badge>
                      </div>
                      {announcement.target_schools?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Sent to {announcement.target_schools.length} school(s)
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Target Schools */}
        <div>
          <Card data-testid="target-schools-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Target Schools
              </CardTitle>
              <CardDescription>Select schools to receive broadcast</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Select All */}
                <div
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectAll ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                  }`}
                  onClick={handleSelectAllToggle}
                  data-testid="select-all-schools"
                >
                  <Checkbox checked={selectAll} />
                  <div>
                    <p className="font-medium">All Schools</p>
                    <p className="text-xs text-muted-foreground">{schools.length} schools</p>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <p className="text-sm text-muted-foreground mb-2">Or select specific schools:</p>
                  {loading ? (
                    <p className="text-center py-4 text-muted-foreground">Loading schools...</p>
                  ) : (
                    <div className="max-h-[400px] overflow-y-auto space-y-2">
                      {schools.map((school) => {
                        const isSelected = formData.target_schools.includes(school.id);
                        return (
                          <div
                            key={school.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => handleSchoolToggle(school.id)}
                            data-testid={`school-${school.school_code}`}
                          >
                            <Checkbox checked={isSelected} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{school.name}</p>
                              <p className="text-xs text-muted-foreground">{school.school_code}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BroadcastPage;
